import React, { useState, useEffect, useRef, useMemo, useCallback, memo, forwardRef } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import LOCALE from './locale';
import { WEEK_NUMBER, PREV_TRANSITION, NEXT_TRANSITION, SELECTOR_YEAR_SET_NUMBER, getDaysArray, getYearSet, formatDateString, isWith1Month, getEndDateItemByDuration } from './const';
import { cx, isValidDate } from './utils';

const TODAY = new Date();
const YEAR = TODAY.getFullYear();
const MONTH = TODAY.getMonth() + 1;
const DATE = TODAY.getDate();

const ITEM_HEIGHT = 40;

interface IObjectKeysAny {
  [key: string]: any;
}
interface IObjectKeysBool {
  [key: string]: boolean;
}
interface IObjectKeysArray {
  [key: string]: Array<object>;
}

// Wrapper component for CSSTransition that manages its own ref (React 19 compatibility)
interface FadeTransitionProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  [key: string]: any;
}
const FadeDiv = forwardRef<HTMLDivElement, FadeTransitionProps>(({ children, className, style, ...props }, ref) => (
  <div ref={ref} className={className} style={style}>{children}</div>
));
const FadeSpan = forwardRef<HTMLSpanElement, FadeTransitionProps>(({ children, className, style, onClick, onMouseDown, onMouseUp, ...props }, ref) => (
  <span ref={ref} className={className} style={style} onClick={onClick} onMouseDown={onMouseDown} onMouseUp={onMouseUp}>{children}</span>
));

const CSSTransitionWithRef: React.FC<{ children: React.ReactElement; [key: string]: any }> = ({ children, ...props }) => {
  const nodeRef = useRef<HTMLElement>(null);
  return (
    <CSSTransition {...props} nodeRef={nodeRef} addEndListener={() => {}}>
      {React.cloneElement(children as any, { ref: nodeRef as any })}
    </CSSTransition>
  );
};
interface IndexProps {
  selected: boolean;
  setSelected: (res: boolean) => void;
  locale?: string;
  defaultDateStart?: string;
  defaultDateEnd?: string;
  rangeDirection?: string;
  startDatePickedArray?: Array<string>;
  endDatePickedArray?: Array<string>;
  markedDates?: Array<string>;
  supportDateRange?: Array<string>;
  duration?: number;
  handleChooseStartDate?: (res: object) => void;
  handleChooseEndDate?: (res: object) => void;
  currentDateObjStart?: IObjectKeysAny;
  setCurrentDateObjStart?: (res: object) => void;
  currentDateObjEnd?: IObjectKeysAny;
  setCurrentDateObjEnd?: (res: object) => void;
  onChooseDate?: (res: object) => void;
}
const Index: React.FC<IndexProps> = memo(
  ({
    selected,
    setSelected,
    locale = 'en-us',
    defaultDateStart = '',
    defaultDateEnd = '',
    rangeDirection = 'start',
    startDatePickedArray = [], // [YY, MM, DD]
    endDatePickedArray = [], // [YY, MM, DD]
    handleChooseStartDate = () => {},
    handleChooseEndDate = () => {},
    currentDateObjStart = {},
    currentDateObjEnd = {},
    setCurrentDateObjStart = () => {},
    setCurrentDateObjEnd = () => {},
    markedDates = [],
    supportDateRange = [],
    duration = 0,
    onChooseDate = () => {},
  }) => {
    const markedDatesHash: IObjectKeysBool = useMemo(() => {
      const res: IObjectKeysBool = {};
      if (markedDates && markedDates.length) {
        let isValid = true;
        for (let i = 0; i < markedDates.length; i += 1) {
          if (!isValidDate(markedDates[i])) {
            isValid = false;
            break;
          }
        }
        if (isValid) {
          markedDates.forEach(d => {
            res[d] = true;
          });
        }
      }
      return res;
    }, [markedDates]);
    const LOCALE_DATA: IObjectKeysAny = useMemo(() => (LOCALE[locale] ? LOCALE[locale] : LOCALE['en-us']), [locale]);
    let defaultDateDateStart = DATE;
    let defaultDateMonthStart = MONTH;
    let defaultDateYearStart = YEAR;

    let defaultDateDateEnd = defaultDateDateStart;
    let defaultDateMonthEnd;
    let defaultDateYearEnd = defaultDateYearStart;

    if (defaultDateMonthStart === 12) {
      defaultDateMonthEnd = 1;
      defaultDateYearEnd = defaultDateYearStart + 1;
    } else {
      defaultDateMonthEnd = defaultDateMonthStart + 1;
    }

    const isDefaultDateValidStart = useMemo(() => isValidDate(defaultDateStart), [defaultDateStart]);
    if (isDefaultDateValidStart) {
      const dateStr = defaultDateStart.split('-');
      defaultDateYearStart = Number(dateStr[0]);
      defaultDateMonthStart = Number(dateStr[1]);
      defaultDateDateStart = Number(dateStr[2]);
    }
    const isDefaultDateValidEnd = useMemo(() => isValidDate(defaultDateEnd), [defaultDateEnd]);
    if (isDefaultDateValidEnd) {
      const dateStr = defaultDateEnd.split('-');
      defaultDateYearEnd = Number(dateStr[0]);
      defaultDateMonthEnd = Number(dateStr[1]);
      defaultDateDateEnd = Number(dateStr[2]);
      // special handle
      if (defaultDateMonthStart === 12) {
        defaultDateMonthEnd = 1;
        defaultDateYearEnd = defaultDateYearStart + 1;
      } else {
        defaultDateMonthEnd = defaultDateMonthStart + 1;
      }
    }

    let showPrevYearArrow = true;
    let showPrevMonthArrow = true;
    let showNextYearArrow = true;
    let showNextMonthArrow = true;

    if (currentDateObjStart.string && currentDateObjEnd.string) {
      if (rangeDirection === 'start') {
        if (isWith1Month(currentDateObjStart.year, currentDateObjEnd.year, currentDateObjStart.month, currentDateObjEnd.month, 'add')) {
          showNextYearArrow = false;
          showNextMonthArrow = false;
        }
      } else {
        if (isWith1Month(currentDateObjEnd.year, currentDateObjStart.year, currentDateObjEnd.month, currentDateObjStart.month, 'minus')) {
          showPrevYearArrow = false;
          showPrevMonthArrow = false;
        }
      }
    }

    const defaultDatesStart = getDaysArray(defaultDateYearStart, defaultDateMonthStart);
    const defaultDatesEnd = getDaysArray(defaultDateYearEnd, defaultDateMonthEnd);

    let defaultDateMonth: number;
    let defaultDateDate;
    let defaultDateYear: number;
    let defaultDates;
    let defaultYearStr;
    let defaultMonthStr;
    let defaultDateStr;

    if (rangeDirection === 'start') {
      defaultDateMonth = defaultDateMonthStart;
      defaultDateDate = defaultDateDateStart;
      defaultDateYear = defaultDateYearStart;
      defaultDates = defaultDatesStart;
      defaultYearStr = formatDateString(defaultDateYearStart);
      defaultMonthStr = formatDateString(defaultDateMonthStart);
      defaultDateStr = formatDateString(defaultDateDateStart);
    } else {
      defaultDateMonth = defaultDateMonthEnd;
      defaultDateDate = defaultDateDateEnd;
      defaultDateYear = defaultDateYearEnd;
      defaultDates = defaultDatesEnd;
      defaultYearStr = formatDateString(defaultDateYearEnd);
      defaultMonthStr = formatDateString(defaultDateMonthEnd);
      defaultDateStr = formatDateString(defaultDateDateEnd);
    }

    useEffect(() => {
      if (rangeDirection === 'start') {
        setCurrentDateObjStart({ year: defaultDateYear, month: defaultDateMonth, string: `${defaultDateYear}-${defaultDateMonth}` });
      } else {
        setCurrentDateObjEnd({ year: defaultDateYear, month: defaultDateMonth, string: `${defaultDateYear}-${defaultDateMonth}` });
      }
    }, [rangeDirection, defaultDateYear, defaultDateMonth]);

    const [dates, setDates] = useState(defaultDates);
    const [pickedYearMonth, setPickedYearMonth] = useState({
      year: defaultYearStr,
      month: defaultMonthStr,
      string: `${defaultYearStr}-${defaultMonthStr}`,
    });
    const [defaultDateObj, setDefaultDateObj] = useState({
      year: defaultYearStr,
      month: defaultMonthStr,
      date: defaultDateStr,
    });
    const [pickedDateInfo, setPickedDateInfo] = useState({
      year: defaultYearStr,
      month: defaultMonthStr,
      date: defaultDateStr,
    });
    const [direction, setDirection] = useState(NEXT_TRANSITION);
    const [yearSelectorPanelList, setYearSelectorPanelList] = useState(getYearSet(defaultDateYear));
    const [yearSelectorPanel, setYearSelectorPanel] = useState(defaultDateYear);
    const [showMask, setShowMask] = useState(false);
    const [showSelectorPanel, setShowSelectorPanel] = useState(false);
    const $monthSelectorPanel = useRef(null);
    const onMouseDown = useCallback(() => {}, []);
    const onMouseUp = useCallback(() => {}, []);
    useEffect(() => {
      setDates(getDaysArray(Number(pickedYearMonth.year), Number(pickedYearMonth.month)));
    }, [pickedYearMonth]);
    const minSupportDate = supportDateRange.length > 0 && isValidDate(supportDateRange[0]) ? supportDateRange[0] : '';
    const maxSupportDate = supportDateRange.length > 1 && isValidDate(supportDateRange[1]) ? supportDateRange[1] : '';
    const pickYear = useCallback(
      (year: number, direction: string) => {
        year = Number(year);
        if (direction === PREV_TRANSITION) {
          year = year - 1;
        } else {
          year = year + 1;
        }
        const newData = { ...pickedYearMonth, year: String(year), string: `${String(year)}-${pickedYearMonth.month}` };
        setPickedYearMonth(newData);
        if (rangeDirection === 'start') {
          setCurrentDateObjStart(newData);
        } else {
          setCurrentDateObjEnd(newData);
        }
        setDirection(direction);
      },
      [pickedYearMonth],
    );
    const pickMonth = useCallback(
      (month: number, direction: string) => {
        month = Number(month);
        let year = Number(pickedYearMonth.year);
        if (direction === PREV_TRANSITION) {
          if (month === 1) {
            month = 12;
            year = year - 1;
          } else {
            month = month - 1;
          }
        } else {
          if (month === 12) {
            month = 1;
            year = year + 1;
          } else {
            month = month + 1;
          }
        }
        const yearStr = String(year);
        const monthStr = formatDateString(month);
        const newData = { ...pickedYearMonth, year: String(year), month: monthStr, string: `${String(year)}-${monthStr}` };
        setPickedYearMonth(newData);
        if (rangeDirection === 'start') {
          setCurrentDateObjStart(newData);
        } else {
          setCurrentDateObjEnd(newData);
        }
        setDirection(direction);
      },
      [pickedYearMonth],
    );
    const pickDate = useCallback((pickedDate: string) => {}, []);
    const changeSelectorPanelYearSet = useCallback((yearSelectorPanel: number, direction: string) => {
      setDirection(direction);
      setYearSelectorPanel(yearSelectorPanel);
      setYearSelectorPanelList(getYearSet(yearSelectorPanel));
    }, []);
    const handleShowSelectorPanel = useCallback(() => {
      setShowSelectorPanel(!showSelectorPanel);
      setShowMask(!showMask);
    }, [showSelectorPanel, showMask]);
    let transitionContainerStyle;
    let content;
    if (dates.length) {
      let row = dates.length / WEEK_NUMBER;
      let rowIndex = 1;
      let rowObj: IObjectKeysArray = {};
      dates.map((item, key) => {
        if (key < rowIndex * WEEK_NUMBER) {
          if (!rowObj[rowIndex]) {
            rowObj[rowIndex] = [];
          }
          rowObj[rowIndex].push(item);
        } else {
          rowIndex = rowIndex + 1;
          if (!rowObj[rowIndex]) {
            rowObj[rowIndex] = [];
          }
          rowObj[rowIndex].push(item);
        }
      });
      content = (
        <CalendarBody
          selected={selected}
          setSelected={setSelected}
          startDatePickedArray={startDatePickedArray}
          endDatePickedArray={endDatePickedArray}
          handleChooseStartDate={handleChooseStartDate}
          handleChooseEndDate={handleChooseEndDate}
          rangeDirection={rangeDirection}
          data={rowObj}
          pickedYearMonth={pickedYearMonth}
          pickedDateInfo={pickedDateInfo}
          onClick={pickDate}
          key={pickedYearMonth.string}
          markedDatesHash={markedDatesHash}
          minSupportDate={minSupportDate}
          maxSupportDate={maxSupportDate}
          duration={duration}
          onChooseDate={onChooseDate}
        />
      );
      transitionContainerStyle = {
        height: `${row * ITEM_HEIGHT}px`,
      };
    }
    const captionHtml = LOCALE_DATA.weeks.map((item: string, key: string) => {
      return (
        <div className={`react-minimal-datetime-range-calendar__table-caption react-minimal-datetime-range-calendar__table-cel no-border`} key={key}>
          {item}
        </div>
      );
    });
    let selectorPanelClass = cx('react-minimal-datetime-range-dropdown', 'react-minimal-datetime-range-calendar__selector-panel', showSelectorPanel && 'visible');
    let selectorPanelMonthHtml = LOCALE_DATA.months.map((item: string, key: string) => {
      let itemMonth: number = Number(key) + 1;
      const numberMonth = Number(pickedYearMonth.month);
      let monthItemClass = cx('react-minimal-datetime-range-dropdown-calendar__month-item', itemMonth == numberMonth && 'active');
      let month = itemMonth - 1;
      let direction = NEXT_TRANSITION;
      if (itemMonth < numberMonth) {
        direction = PREV_TRANSITION;
        month = itemMonth + 1;
      }
      return (
        <div
          className={monthItemClass}
          onClick={
            itemMonth !== numberMonth
              ? () => pickMonth(month, direction)
              : () => {
                  return;
                }
          }
          key={key}
        >
          <div>{item}</div>
        </div>
      );
    });
    let selectorPanelYearHtml;
    if (yearSelectorPanelList.length) {
      selectorPanelYearHtml = yearSelectorPanelList.map((item, key) => {
        const numberYearMonth = Number(pickedYearMonth.year);
        let yearItemClass = cx('react-minimal-datetime-range-dropdown-calendar__year-item', item == numberYearMonth && 'active');
        let year = item - 1;
        let direction = NEXT_TRANSITION;
        if (item < numberYearMonth) {
          direction = PREV_TRANSITION;
          year = item + 1;
        }
        return (
          <div
            className={yearItemClass}
            onClick={
              item !== numberYearMonth
                ? () => pickYear(year, direction)
                : () => {
                    return;
                  }
            }
            key={key}
          >
            <span style={{ verticalAlign: 'middle' }}>{item}</span>
          </div>
        );
      });
    }
    const classNames = direction == NEXT_TRANSITION ? 'forward' : 'backward';
    return (
      <div className={cx('react-minimal-datetime-range-calendar', 'react-minimal-datetime-range-calendar--range')}>
        <div className={`react-minimal-datetime-range-calendar__header`}>
          <div className={selectorPanelClass} ref={$monthSelectorPanel} onMouseDown={onMouseDown} onMouseUp={onMouseUp} onTouchEnd={onMouseDown} onTouchCancel={onMouseUp}>
            <div className={`react-minimal-datetime-range-dropdown-calendar__menu`}>
              <div className={`react-minimal-datetime-range-dropdown-calendar__month`}>{selectorPanelMonthHtml}</div>
              <div style={{ height: '10px' }} />
              <div className={`react-minimal-datetime-range__col react-minimal-datetime-range__col-0-5`}>
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  style={{ verticalAlign: 'middle' }}
                  onClick={() => changeSelectorPanelYearSet(yearSelectorPanel - SELECTOR_YEAR_SET_NUMBER, PREV_TRANSITION)}
                >
                  <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                  <path d="M0 0h24v24H0z" fill="none" />
                </svg>
              </div>
              <div className={`react-minimal-datetime-range__col react-minimal-datetime-range__col-9`}>
                <TransitionGroup className="react-minimal-datetime-range-calendar__selector-panel-year-set-container" childFactory={(child: any) => React.cloneElement(child, { classNames })}>
                  <CSSTransitionWithRef key={yearSelectorPanelList.join('-')} timeout={{ enter: 300, exit: 300 }} classNames={classNames}>
                    <FadeDiv className={`react-minimal-datetime-range-dropdown-calendar__year`}>{selectorPanelYearHtml}</FadeDiv>
                  </CSSTransitionWithRef>
                </TransitionGroup>
              </div>
              <div className={`react-minimal-datetime-range__col react-minimal-datetime-range__col-0-5`}>
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  style={{ verticalAlign: 'middle' }}
                  onClick={() => changeSelectorPanelYearSet(yearSelectorPanel + SELECTOR_YEAR_SET_NUMBER, NEXT_TRANSITION)}
                >
                  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                  <path d="M0 0h24v24H0z" fill="none" />
                </svg>
              </div>
            </div>
          </div>
          <div className={`react-minimal-datetime-range__col react-minimal-datetime-range__col-3`}>
            {showPrevYearArrow && (
              <div className={`react-minimal-datetime-range__col react-minimal-datetime-range-calendar__previous`} onClick={() => pickYear(Number(pickedYearMonth.year), PREV_TRANSITION)}>
                <svg width="15" height="15" viewBox="0 0 24 24">
                  <path d="M18.41 16.59L13.82 12l4.59-4.59L17 6l-6 6 6 6zM6 6h2v12H6z" />
                  <path fill="none" d="M24 24H0V0h24v24z" />
                </svg>
              </div>
            )}
            {showPrevMonthArrow && (
              <div className={`react-minimal-datetime-range__col react-minimal-datetime-range-calendar__sub-previous`} onClick={() => pickMonth(Number(pickedYearMonth.month), PREV_TRANSITION)}>
                <svg width="15" height="15" viewBox="0 0 24 24">
                  <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                  <path d="M0 0h24v24H0z" fill="none" />
                </svg>
              </div>
            )}
          </div>
          <div className={`react-minimal-datetime-range__col react-minimal-datetime-range__col-6`}>
            <TransitionGroup className="react-minimal-datetime-range-calendar__title-container" childFactory={(child: any) => React.cloneElement(child, { classNames })}>
              <CSSTransitionWithRef key={pickedYearMonth.string} timeout={{ enter: 300, exit: 300 }} classNames={classNames}>
                <FadeSpan className={`react-minimal-datetime-range-calendar__title react-minimal-datetime-range-calendar__clicker`} style={{ left: '0' }} onClick={handleShowSelectorPanel} onMouseDown={onMouseDown} onMouseUp={onMouseUp}>
                  <span className={`react-minimal-datetime-range-calendar__clicker`}>{LOCALE_DATA.date_format(LOCALE_DATA.months[Number(pickedYearMonth.month) - 1], pickedYearMonth.year)}</span>
                </FadeSpan>
              </CSSTransitionWithRef>
            </TransitionGroup>
          </div>
          <div className={`react-minimal-datetime-range__col react-minimal-datetime-range__col-3`}>
            {showNextMonthArrow && (
              <div className={`react-minimal-datetime-range__col react-minimal-datetime-range-calendar__next`} onClick={() => pickMonth(Number(pickedYearMonth.month), NEXT_TRANSITION)}>
                <svg width="15" height="15" viewBox="0 0 24 24">
                  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                  <path d="M0 0h24v24H0z" fill="none" />
                </svg>
              </div>
            )}
            {showNextYearArrow && (
              <div className={`react-minimal-datetime-range__col react-minimal-datetime-range-calendar__sub-next`} onClick={() => pickYear(Number(pickedYearMonth.year), NEXT_TRANSITION)}>
                <svg width="15" height="15" viewBox="0 0 24 24">
                  <path d="M5.59 7.41L10.18 12l-4.59 4.59L7 18l6-6-6-6zM16 6h2v12h-2z" />
                  <path fill="none" d="M0 0h24v24H0V0z" />
                </svg>
              </div>
            )}
          </div>
        </div>
        <div className={`react-minimal-datetime-range-calendar__content`}>
          <div className={`react-minimal-datetime-range-calendar__table`}>
            <div className={`react-minimal-datetime-range-calendar__table-row`}>{captionHtml}</div>
          </div>
          <TransitionGroup className={`react-minimal-datetime-range-calendar__body-container`} style={transitionContainerStyle} childFactory={(child: any) => React.cloneElement(child, { classNames })}>
            <CSSTransitionWithRef key={pickedYearMonth.string} timeout={{ enter: 300, exit: 300 }} classNames={classNames}>
              <FadeDiv>{content}</FadeDiv>
            </CSSTransitionWithRef>
          </TransitionGroup>
        </div>
      </div>
    );
  },
);
interface pickedDateInfo {
  date: string;
  month: string;
  year: string;
}
interface pickedYearMonth {
  month: string;
  year: string;
}
interface CalendarBodyProps {
  selected: boolean;
  setSelected: (res: boolean) => void;
  startDatePickedArray: Array<string>;
  endDatePickedArray: Array<string>;
  handleChooseStartDate: (res: object) => void;
  handleChooseEndDate: (res: object) => void;
  rangeDirection: string;
  data?: IObjectKeysArray;
  pickedDateInfo?: pickedDateInfo;
  pickedYearMonth?: pickedYearMonth;
  markedDates?: Array<string>;
  duration?: number;
  markedDatesHash: IObjectKeysBool;
  minSupportDate: string;
  maxSupportDate: string;
  onClick?: (res: string) => void;
  onChooseDate?: (res: object) => void;
}
const CalendarBody: React.FC<CalendarBodyProps> = memo(
  ({
    selected,
    setSelected,
    startDatePickedArray,
    endDatePickedArray,
    handleChooseStartDate,
    handleChooseEndDate,
    rangeDirection,
    data = {},
    pickedDateInfo = {},
    pickedYearMonth = {},
    markedDatesHash = {},
    minSupportDate,
    maxSupportDate,
    duration,
    onChooseDate,
  }) => {
    const content = Object.keys(data).map(key => {
      let colHtml;
      if (data[key].length) {
        colHtml = data[key].map((item: { [k: string]: any }, key: any) => {
          const itemDate = new Date(`${item.year}-${item.month}-${item.name}`);
          let isDisabled = pickedYearMonth.month !== item.month;
          if (minSupportDate) {
            if (new Date(itemDate) < new Date(minSupportDate)) {
              isDisabled = true;
            }
          }
          if (maxSupportDate) {
            if (new Date(itemDate) > new Date(maxSupportDate)) {
              isDisabled = true;
            }
          }
          let isPickedStart = false;
          let isPickedEnd = false;
          let isHighlight = false;
          if (isDisabled === false) {
            let starts = startDatePickedArray;
            let ends = endDatePickedArray;
            if (startDatePickedArray.length && endDatePickedArray.length) {
              const a = new Date(startDatePickedArray.join('-'));
              const b = new Date(endDatePickedArray.join('-'));
              starts = a < b ? startDatePickedArray : endDatePickedArray;
              ends = a > b ? startDatePickedArray : endDatePickedArray;
            }
            if (starts.length) {
              isPickedStart = starts[0] === item.year && starts[1] === item.month && starts[2] === item.name;
              const targetDate = new Date(starts.join('-'));
              if (!ends.length) {
                if (itemDate > targetDate) {
                  isHighlight = true;
                }
              } else {
                if (itemDate > targetDate && itemDate < new Date(ends.join('-'))) {
                  isHighlight = true;
                }
              }
            }
            if (ends.length) {
              isPickedEnd = ends[0] === item.year && ends[1] === item.month && ends[2] === item.name;
            }
          }
          const datePickerItemClass = cx(
            'react-minimal-datetime-range-calendar__table-cel',
            'react-minimal-datetime-range-calendar__date-item',
            'range',
            isDisabled && 'disabled',
            isPickedStart && 'active',
            isPickedEnd && 'active',
            isHighlight && 'highlight',
            DATE == item.name && MONTH == item.month && YEAR == item.year && 'today',
            markedDatesHash[`${item.year}-${item.month}-${item.name}`] && 'marked',
          );
          return (
            <CalendarItem
              key={key}
              selected={selected}
              setSelected={setSelected}
              startDatePickedArray={startDatePickedArray}
              endDatePickedArray={endDatePickedArray}
              handleChooseStartDate={handleChooseStartDate}
              handleChooseEndDate={handleChooseEndDate}
              item={item}
              isDisabled={isDisabled}
              datePickerItemClass={datePickerItemClass}
              duration={duration}
              onChooseDate={onChooseDate}
            />
          );
        });
      }
      return (
        <div className={`react-minimal-datetime-range-calendar__table-row`} key={key}>
          {colHtml}
        </div>
      );
    });
    return <div className={`react-minimal-datetime-range-calendar__table slide`}>{content}</div>;
  },
);
interface CalendarItemProps {
  selected: boolean;
  setSelected: (res: boolean) => void;
  startDatePickedArray: Array<string>;
  endDatePickedArray: Array<string>;
  handleChooseStartDate: (res: object) => void;
  handleChooseEndDate: (res: object) => void;
  item?: IObjectKeysAny;
  isPicked?: boolean;
  isDisabled?: boolean;
  datePickerItemClass?: string;
  duration?: number;
  onChooseDate?: (res: object) => void;
}
const CalendarItem: React.FC<CalendarItemProps> = memo(
  ({
    selected,
    setSelected,
    startDatePickedArray,
    endDatePickedArray,
    handleChooseStartDate,
    handleChooseEndDate,
    item = {},
    isDisabled = false,
    datePickerItemClass = '',
    duration = 0,
    onChooseDate,
  }) => {
    const handleDuration = useCallback(() => {
      const endDateItem = getEndDateItemByDuration(item, duration);
      handleChooseEndDate(endDateItem);
      handleChooseStartDate(item);
      setSelected(true);
    }, [item, duration]);
    const handleOnClick = useCallback(() => {
      if (isDisabled) return;
      onChooseDate && onChooseDate(item);
      if (startDatePickedArray.length) {
        setSelected(true);
        handleChooseEndDate(item);
      } else {
        if (duration > 0) {
          handleDuration();
        } else {
          handleChooseStartDate(item);
          return;
        }
      }
      if (selected) {
        if (duration > 0) {
          handleDuration();
        } else {
          handleChooseEndDate({ year: '', month: '', name: '', value: '' });
          handleChooseStartDate(item);
          setSelected(false);
        }
      }
    }, [item, selected, startDatePickedArray, endDatePickedArray, duration]);
    const handleOnMouseOver = useCallback(() => {
      if (duration > 0) return;
      if (isDisabled) return;
      if (!selected) {
        if (startDatePickedArray.length) {
          handleChooseEndDate(item);
        }
      }
    }, [item, selected, startDatePickedArray, endDatePickedArray, duration]);
    return (
      <div className={`${datePickerItemClass}`} onMouseOver={handleOnMouseOver} onClick={handleOnClick}>
        {item.name}
      </div>
    );
  },
);
export default Index;
